'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

/** Sub-pixel scroll positions never land exactly on 0 or on the maximum. */
const EPSILON = 4;

type Direction = -1 | 1;

/**
 * How far the next item in that direction sits from the snap line — null past
 * the last one. Items snap to the scroll padding, not to the track's edge;
 * measured from the edge, a step would only cover the padding.
 */
function stepOffset(track: HTMLElement, direction: Direction) {
    const scrollPadding = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
    const snapLine = track.getBoundingClientRect().left + scrollPadding;
    const offsets = Array.from(track.children).map((item) => item.getBoundingClientRect().left - snapLine);

    if (direction === -1) offsets.reverse();

    return offsets.find((offset) => offset * direction > EPSILON) ?? null;
}

/** Scroll position and step navigation for a snap-scrolling track. */
export function useUiSlider(itemCount: number) {
    const trackRef = useRef<HTMLUListElement | null>(null);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const scrollStep = useCallback((direction: Direction) => {
        const track = trackRef.current;
        if (!track) return;

        const offset = stepOffset(track, direction);

        // No item left to align to: run out to the end of the track instead.
        if (offset === null) {
            track.scrollTo({ left: direction === 1 ? track.scrollWidth : 0, behavior: 'smooth' });
            return;
        }

        track.scrollBy({ left: offset, behavior: 'smooth' });
    }, []);

    const scrollPrev = useCallback(() => scrollStep(-1), [scrollStep]);
    const scrollNext = useCallback(() => scrollStep(1), [scrollStep]);

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLElement>) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

            event.preventDefault();
            scrollStep(event.key === 'ArrowLeft' ? -1 : 1);
        },
        [scrollStep],
    );

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;

        let raf = 0;

        const update = () => {
            setCanScrollPrev(track.scrollLeft > EPSILON);
            setCanScrollNext(track.scrollLeft + track.clientWidth < track.scrollWidth - EPSILON);
        };

        const scheduleUpdate = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(update);
        };

        update();

        track.addEventListener('scroll', scheduleUpdate, { passive: true });

        // The track is a grid above the breakpoint and a slider below it, so the
        // buttons follow the resize, not just the scroll.
        const observer = new ResizeObserver(scheduleUpdate);
        observer.observe(track);

        return () => {
            cancelAnimationFrame(raf);
            track.removeEventListener('scroll', scheduleUpdate);
            observer.disconnect();
        };
    }, [itemCount]);

    return { trackRef, canScrollPrev, canScrollNext, scrollPrev, scrollNext, onKeyDown };
}
